import { Component, ElementRef, Input, OnDestroy, OnInit, Type, ViewChild, computed, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalWindowsTypes } from 'src/app/core/enums';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AutosaveManagerService } from 'src/app/services/autosave/autosave-manager.service';
import { CompletedExamCreationService } from 'src/app/services/exam/completed-exam-creation.service';
import { ExamModeRestrictionsService } from 'src/app/services/exam/exam-mode-restrictions.service';
import { PdfInstanceManagerService } from 'src/app/services/instance/pdf-instance-manager.service';
import { ExamService } from 'src/app/services/restrictions/exam.service';
import { IModalWindow, ISetOptions, IGetClass } from 'src/app/utils/interfaces/modal-dialog.interface';
import { environment } from 'src/environments/environment';
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from '../message-dialog/message-dialog.component';
import { ReportExamService } from 'src/app/services/exam/report-exam.service';
import { ReportLogLevel } from 'src/app/services/exam/enums/report-level.enum';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Core } from '@pdftron/webviewer';

@Component({
    selector: 'asg-completed-exam-preview-dialog',
    templateUrl: './completed-exam-preview-dialog.component.html',
    styleUrls: ['./completed-exam-preview-dialog.component.scss'],
    standalone: true,
    imports: [FormsModule, NgClass, TranslateModule]
})
export class CompletedExamPreviewDialogComponent implements OnInit, OnDestroy, IModalWindow, ISetOptions, IGetClass {
  @Input() no: () => void;
  @Input() ok: () => void;

  @ViewChild('pdfPreview') pdfPreview: ElementRef;
  isLoading = true;
  index = 0;
  pageCount = 0;
  isConnectToCloud = false;
  examChecked = signal(false);
  sendClicked = signal(false);
  readyToSubmit = computed(() => this.examChecked() && !this.sendClicked());

  private currentDoc: Core.Document;
  private canvasIds: number[] = [];

  constructor(
    private readonly pdfService: PdfInstanceManagerService,
    private readonly dialog: MatDialog,
    private readonly completedExamCreationService: CompletedExamCreationService,
    private readonly examService: ExamService,
    private readonly examModeRestrictionsService: ExamModeRestrictionsService,
    private readonly authService: AuthService,
    private readonly autoSave: AutosaveManagerService,
    private readonly reportExamService: ReportExamService,
    private readonly translate: TranslateService,
  ) {
    this.isConnectToCloud = this.completedExamCreationService.isCloudConnected;
  }

  ngOnInit(): void {
    this.completedExamCreationService.createCompletedExamFile()
      .then(pdf => {
        this.openCompletedExam(pdf);
      })
      .catch(error => console.error(error));
  }

  ngOnDestroy(): void {
    // cancel all load canvases
    if (this.currentDoc) {
      this.canvasIds.forEach(id => {
        this.currentDoc.cancelLoadCanvas(id);
      });
    }
    this.canvasIds = [];
  }

  getWindowType(): Type<IModalWindow> {
    return CompletedExamPreviewDialogComponent;
  }

  getWindowEnumType(): ModalWindowsTypes {
    return ModalWindowsTypes.COMPLETED_EXAM_PREVIEW_DIALOG;
  }

  getInstanceOf(type: string): IModalWindow | ISetOptions | IGetClass | null {
    switch (type) {
      case 'IModalWindow':
        return this as unknown as IModalWindow;
      case 'ISetOptions':
        return this as unknown as ISetOptions;
      case 'IGetClass':
        return this as unknown as IGetClass;
      default: return null;
    }
  }

  setOptions(options: any): void {
    options = options || {};
  }

  getClass(): string {
    return 'completed-exam-preview-dlg';
  }

  goBack(): void {
    this.no();
  }

  async save(): Promise<void> {
    this.confirmSave()
      .then(async () => {
        this.sendClicked.set(true);
        await this.examService.submitExam().then(async () => {
          await this.examModeRestrictionsService.warnUserExamSubmitted();
          await this.reportExamService.sendTemplateMessageReport(
            ReportLogLevel.Information, 'WOLAND.REPORT_TEMPLATES.SUBMIT_EXAM_TEMPLATE', {
              comment: this.translate.instant('WOLAND.REPORT_TEMPLATES.SUBMIT_BY_STUDENT') }
            );
          this.examModeRestrictionsService.stopExam();
          await this.autoSave.deleteAllSavedFiles();
          await this.authService.logout();
        });
      });
  }

  private async openCompletedExam(completedExam: File): Promise<void> {
    const options = {
      l: environment.pdfTronLicenceKey,
      extension: 'pdf'
    };

    this.currentDoc = await (window as any).Core.createDocument(completedExam, options);
    this.pageCount = this.currentDoc.getPageCount();
    const promises: Promise<void>[] = [];
    const canvases: { index: any, canvas: any }[] = [];

    for (let i = 0; i < this.pageCount; i++) {
      promises.push(
        new Promise(resolve => {
          const pageNumber = i + 1;
          return this.currentDoc.requirePage(pageNumber)
            .then(() => {
              const canvasId = Number(this.currentDoc.loadCanvas({
                pageNumber,
                drawComplete: async (canvas, index) => {
                  // draw the annotations back in
                  const currentInstance = this.pdfService.getDocViewer(this.completedExamCreationService.getTabID());
                  if (currentInstance) {
                    await currentInstance.getAnnotationManager().drawAnnotations({ pageNumber: pageNumber, overrideCanvas: canvas });
                  }

                  // add the canvas
                  canvases.push({ index, canvas });
                  this.index = canvases.length;

                  // remove canvas id from list
                  const canvasIdIndex = this.canvasIds.indexOf(canvasId);
                  if (canvasIdIndex !== -1) {
                    this.canvasIds.splice(canvasIdIndex, 1);
                  }

                  resolve();
                },
              }));
              this.canvasIds.push(canvasId);
            });
        })
      );
    }

    Promise.all(promises)
      .then(() => {
        this.canvasIds = [];
        canvases.sort((a, b) => a.index - b.index).forEach(o => {
          const canvas = o.canvas as HTMLElement;
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          this.pdfPreview.nativeElement.appendChild(canvas);
        });
        this.isLoading = false;
      });
  }

  private confirmSave(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const data: MessageDialogData = {
        title: 'COMPLETED_EXAM_SAVE_CONFIRMATION_DIALOG.TITLE',
        message: 'COMPLETED_EXAM_SAVE_CONFIRMATION_DIALOG.MESSAGE',
        actions: [
          {
            title: 'COMPLETED_EXAM_SAVE_CONFIRMATION_DIALOG.CONFIRM',
            color: MessageDialogActionColor.Primary,
            handler: () => resolve()
          },
          {
            title: 'COMPLETED_EXAM_SAVE_CONFIRMATION_DIALOG.CANCEL',
            color: MessageDialogActionColor.None,
            handler: () => reject('canceled')
          }
        ]
      };
      this.dialog.open(MessageDialogComponent, { data });
    });
  }

}
